import type { OneMedia, OneMediaWithErrors } from '@prismamedia/one-components';
import {
  MediaType,
  MultimediaManager,
  getImageUrl,
  mediaFromIframely,
} from '@prismamedia/one-components';
import type { FC } from 'react';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDeepCompareEffect, usePrevious } from 'react-use';
import { ArticleStatus, BrandKey } from '../../__generated__/queries-web';
import { useFocusPointGetter } from '../../apollo/queries/focusPoint.image.graphql';
import { searchImageSourceCategories } from '../../apollo/sharedQueries/getSourcesCategories.web.graphql';
import { history } from '../../history';
import { embedImage } from '../../utils/upload';
import { isPage } from '../../utils/url';
import { postImages, useFormats, usePlugins } from './Plugins';
import { EMPTY_FORMAT, MEDIA_TYPES } from './constants';

interface MediaInputProps {
  allowedMedias: MEDIA_TYPES[];
  allowsMultipleImgs?: boolean;
  brandKey: BrandKey;
  disableDropConfig?: boolean;
  disableImgWidth?: boolean;
  isVideo?: boolean;
  multimediaManagerProps: Pick<
    React.ComponentPropsWithoutRef<typeof MultimediaManager>,
    'Header' | 'disableFields' | 'medias' | 'dropText' | 'title' | 'width'
  >;
  onChange: (field: string, medias: OneMediaWithErrors[]) => void;
  status?: ArticleStatus;
  subjectId?: string;
  videoId?: string | null;
  withErrors?: boolean;
  requiredFormats?: boolean;
}

const reloadMedia = async (targetMedia: OneMedia): Promise<OneMedia> => {
  const iframely = await embedImage(targetMedia.iframely.meta.canonical);
  return mediaFromIframely(iframely);
};

export const MediaInput: FC<MediaInputProps> = memo(
  ({
    allowedMedias,
    allowsMultipleImgs,
    brandKey,
    disableDropConfig,
    disableImgWidth = false,
    isVideo,
    multimediaManagerProps: {
      disableFields,
      dropText,
      medias,
      width = 800,
      ...restMultimediaManagerProps
    },
    onChange,
    status,
    subjectId,
    videoId,
    withErrors,
    requiredFormats,
  }) => {
    const isFirstRender = useRef(true);
    const formats = useFormats(brandKey);
    const getFocusPoint = useFocusPointGetter();
    const [innerMedias, setInnerMedias] = useState<OneMedia[]>(medias);

    const isReady = [
      ArticleStatus.Published,
      ArticleStatus.Private,
      ArticleStatus.Ready,
    ].some((articleStatus) => articleStatus === status);
    const previousValue = usePrevious<boolean>(isReady);

    const plugins = usePlugins(
      allowedMedias,
      brandKey,
      subjectId,
      (isReady && videoId) || undefined,
      {
        allowsMultipleImgs,
        disableImgWidth,
      },
    );

    useEffect(() => {
      if (
        isVideo &&
        isReady &&
        previousValue !== undefined &&
        isReady !== previousValue
      ) {
        window.location.reload();
      }
    }, [isReady, isVideo, previousValue]);

    useDeepCompareEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      onChange('medias', innerMedias);
    }, [innerMedias]);

    useEffect(() => {
      setInnerMedias(medias);
    }, [medias]);

    return (
      <MultimediaManager
        allowsMultipleImgs={allowsMultipleImgs}
        {...restMultimediaManagerProps}
        {...(!disableDropConfig && {
          dropConfig: {
            disableImgWidth,
            handleDropFiles: (files: File[]) => postImages(brandKey)(files),
            mediaType: MediaType.Image,
          },
        })}
        disableFields={disableFields}
        fetchFocusPoint={async (data) => {
          const imageUrl = getImageUrl(data);
          const imageHeight = data.iframely.meta.height;
          const imageWidth = data.iframely.meta.width;
          const res = await getFocusPoint({
            imageUrl,
            imageHeight,
            imageWidth,
          });
          return res.getFocusPoint;
        }}
        fetchSources={searchImageSourceCategories}
        fieldsRequired
        formats={formats}
        medias={innerMedias.map((media) => ({
          ...media,
          format:
            media.format ||
            formats?.find(({ label }) => label === EMPTY_FORMAT)?.value,
        }))}
        plugins={plugins}
        reloadMedia={reloadMedia}
        setMedias={setInnerMedias}
        withErrors={withErrors}
        width={width}
        requiredFormats={requiredFormats ?? !!isPage(history.location.pathname)}
        viewMedia={(idx) => window.open(medias[idx].iframely.meta.canonical)}
        disableCrop
      />
    );
  },
);
